// Bearer session token for the Owletto extension's embedded side-panel iframe.
//
// The extension embeds owletto-web as a CROSS-SITE iframe (top-level origin
// chrome-extension://…). A session cookie is unreliable inside that partition,
// so the gateway's /api/extension-bootstrap page exchanges the extension's
// deep-link token for a Better Auth session token and stashes it in
// sessionStorage under this key before handing control to the SPA. The SPA then
// sends it as `Authorization: Bearer` on every API + auth call (the cloud auth
// bridge passes request headers to Better Auth, and the bearer() plugin honours
// the header). Empty for normal first-party web sessions, which keep using the
// session cookie unchanged.
const STORAGE_KEY = 'owletto.session_token';

let cached: string | null | undefined;

/**
 * The embedded Bearer session token, or '' when not running embedded (normal
 * web). Read once and memoized — the bootstrap writes it before the SPA boots.
 * '' is the documented "no token" sentinel for better-auth's fetchOptions.auth.
 */
export function getEmbeddedSessionToken(): string {
  if (cached === undefined) {
    try {
      cached =
        typeof window !== 'undefined' ? window.sessionStorage.getItem(STORAGE_KEY) : null;
    } catch {
      cached = null;
    }
  }
  return cached ?? '';
}

/**
 * True when the SPA is running inside the extension's cross-site side-panel
 * iframe — independent of whether a Bearer token is present.
 *
 * Why this exists separately from the token: when the extension's deep-link
 * token is expired/absent, the bootstrap can't mint a session token, so
 * sessionStorage stays empty. If we keyed "embedded?" on the token alone, that
 * case would fall back to the normal-web auth path (Better Auth's useSession),
 * which NEVER settles in this storage partition — a permanent loading spinner.
 * Detecting the iframe by its chrome-extension:// ancestor keeps us on the
 * settling Bearer/cookie query path even token-less, so the panel resolves to
 * either authenticated (cookie still valid) or a clean signed-out screen instead
 * of hanging.
 */
export function isEmbeddedContext(): boolean {
  if (typeof window === 'undefined') return false;
  // A stashed token means the bootstrap ran — unambiguously embedded.
  if (getEmbeddedSessionToken()) return true;
  // Token-less: detect the iframe by its extension ancestor. ancestorOrigins is
  // Chromium-only (the extension is Chrome-only) and lists each frame ancestor's
  // origin; the side panel's is chrome-extension://<id>.
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors) {
      for (let i = 0; i < ancestors.length; i++) {
        if (ancestors[i].startsWith('chrome-extension://')) return true;
      }
    }
  } catch {
    // ancestorOrigins unavailable — treat as non-embedded.
  }
  return false;
}

/**
 * Drop the embedded token on sign-out so the panel can't keep authenticating as
 * the signed-out user until the iframe remounts. Clears both the memo and the
 * stored value.
 */
export function clearEmbeddedSessionToken(): void {
  cached = '';
  try {
    if (typeof window !== 'undefined') window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore — sessionStorage may be unavailable
  }
}
